import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { Heading, IconButton, Paragraph, Popover, TextButton } from '@zillow/constellation-10';
import { IconLockClosedOutline } from '@zillow/constellation-icons';

import { useSelector } from '../../../../../selectors/useSelector';

import type { MouseEvent, ReactNode } from 'react';
import type { TextButtonPropsInterface } from '@zillow/constellation-10';

const STRING_TYPE = 'string';

type GetDisableEditInfoProps = {
  isFeed: boolean;
  allowFeedEdit: boolean;
  canUpdateListing: boolean;
  isArchived: boolean;
  isUpdateListingRestricted: boolean;
  disabledMessage: string | ReactNode;
};

const getDisableEditInfo = ({
  isFeed,
  allowFeedEdit,
  canUpdateListing,
  isArchived,
  isUpdateListingRestricted,
  disabledMessage,
}: GetDisableEditInfoProps): string | ReactNode => {
  switch (true) {
    case isFeed && !allowFeedEdit:
      return 'To change this information, go to your listing feed.';
    case !canUpdateListing:
      return `This information can't be edited. For assistance, contact Customer Support.`;
    case isArchived:
      return 'To change this information, unarchive and update the listing.';
    case isUpdateListingRestricted:
      return `This information can't be edited. For assistance, contact Customer Support.`;
    case !!disabledMessage:
      return disabledMessage;
    default:
      return '';
  }
};

const DEFAULT_EDIT_BUTTON_TEXT = 'Edit';
const LOCKED_ARIA_DESCRIBED_BY = 'locked_aria-describedby';
const DISABLED_ARIA_LABEL = 'disabled-aria-label';
const ENABLED_ARIA_LABEL = 'enabled-aria-label';

type Props = {
  allowFeedEdit?: boolean;
  isDisabled?: boolean;
  useLockIcon?: boolean;
  buttonFieldName?: string;
  editButtonText?: string;
  popoverHeading?: string;
  value?: string;
  disabledMessage?: string | ReactNode;
  onEditClick: ((e: MouseEvent<HTMLButtonElement>) => void) | (() => void);
  dataEventId?: string;
  tone?: TextButtonPropsInterface['tone'];
  textStyle?: TextButtonPropsInterface['textStyle'];
  size?: TextButtonPropsInterface['size'];
};

const ContentSectionEditButton = ({
  allowFeedEdit = false,
  isDisabled: isDisabledFromParent = false,
  useLockIcon = false,
  tone = 'brand',
  buttonFieldName = '',
  editButtonText = DEFAULT_EDIT_BUTTON_TEXT,
  textStyle = 'body-sm-bold',
  size = 'md',
  popoverHeading = '',
  value = '',
  disabledMessage = '',
  onEditClick,
  dataEventId,
}: Props): JSX.Element => {
  const {
    user: {
      features: { canUpdateListing },
    },
    properties: {
      currentProperty: {
        isFeed,
        isArchived,
        restrictions: { isUpdateListingRestricted },
      },
    },
  } = useSelector((state) => ({ user: state.user, properties: state.properties }), shallowEqual);

  const isDisabled =
    isDisabledFromParent ||
    (isFeed && !allowFeedEdit) ||
    !canUpdateListing ||
    isArchived ||
    isUpdateListingRestricted ||
    useLockIcon;

  const disabledEditInfo = useMemo(() => {
    return getDisableEditInfo({
      isFeed,
      allowFeedEdit,
      canUpdateListing,
      isArchived,
      isUpdateListingRestricted,
      disabledMessage,
    });
  }, [isDisabled]);
  const ariaLabels = useMemo(
    () => ({
      [LOCKED_ARIA_DESCRIBED_BY]: buttonFieldName ? `locked-edit-button-for-${buttonFieldName}` : undefined,
      [DISABLED_ARIA_LABEL]: buttonFieldName ? `disabled-edit-button-for-${buttonFieldName}` : undefined,
      [ENABLED_ARIA_LABEL]: buttonFieldName ? `edit-button-for-${buttonFieldName}` : undefined,
    }),
    [buttonFieldName],
  );

  return isDisabled ? (
    <Popover
      header={<Heading level={4}>{popoverHeading || 'Edits are disabled'}</Heading>}
      body={typeof disabledEditInfo === STRING_TYPE ? <Paragraph>{disabledEditInfo}</Paragraph> : disabledEditInfo}
      tone="neutral"
      trigger={
        useLockIcon ? (
          <IconButton
            emphasis="bare"
            title={ariaLabels[LOCKED_ARIA_DESCRIBED_BY] ?? ''}
            size="md"
            css={{ marginRight: '-300' }}
          >
            <IconLockClosedOutline />
          </IconButton>
        ) : (
          <TextButton
            aria-label={ariaLabels[DISABLED_ARIA_LABEL]}
            textStyle={textStyle}
            tone={tone}
            size={size}
            data-eventid={dataEventId}
            css={{ cursor: 'help', opacity: 'disabled' }}
          >
            {editButtonText}
          </TextButton>
        )
      }
    />
  ) : (
    <TextButton
      aria-label={ariaLabels[ENABLED_ARIA_LABEL]}
      textStyle={textStyle}
      tone={tone}
      onClick={onEditClick}
      value={value}
      size={size}
      data-eventid={dataEventId}
    >
      {editButtonText}
    </TextButton>
  );
};

export default ContentSectionEditButton;
